import React, {
  useCallback, useState, useMemo, useEffect, useRef,
} from 'react';
import {
  arrayOf, bool, number, object, oneOfType, string, shape, func, instanceOf,
} from 'prop-types';
import I18n from 'i18n-js';
import { Input, Spin, Tree } from 'antd';
import { debounce } from 'lodash-es';
import airbrakeClient from 'configuredAirbrakeClient';
import FaIcon from 'components/fa-icon';
import Item from 'components/top-menu-filter/nested/Item';
import modifyFilterListStyles from 'components/top-menu-filter/helpers/modifyFilterListStyles';
import setSearchInputFocus from 'components/top-menu-filter/helpers/setSearchInputFocus';
import prepareSearchApi from 'components/top-menu-filter/helpers/prepareSearchApi';
import defineFilteredItems from 'components/top-menu-field-groups/helpers/groupabble_setting_helpers';

const STATIC_OFFSET = 54;

const FieldGroupsSettings = ({
  checkedKeys = null,
  items = [],
  onCheck = null,
  onSelect = null,
  plainItemsIndex = new Map(),
  selectedKeys = [],
  visible,
  expandAll = true,
  expandKeys = [],
  drawerHeight,
}) => {
  const debounceTimeout = plainItemsIndex.size > 10_000 ? 500 : 100;
  const filterWrapperRef = useRef(null);

  const [searchValue, setSearchValue] = useState('');
  const [searching, setSearching] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);

  const searchApi = useMemo(() => prepareSearchApi(plainItemsIndex), [plainItemsIndex]);

  const settings = [
    searchApi, setFilteredItems, setSearching, items, plainItemsIndex,
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateFilteredItems = useCallback(debounce(async (filter) => {
    try {
      setSearching(true);
      defineFilteredItems(filter, settings);
    } catch (error) {
      airbrakeClient.notify({ error });
    }
  }, debounceTimeout), []);

  const onChangeSearchValue = useCallback(({ target }) => {
    const filter = target.value;
    setSearchValue(filter);

    if (!filter) { setFilteredItems(items); return; }

    updateFilteredItems(filter);
  }, [updateFilteredItems, items]);

  useEffect(() => {
    if (!visible) { return; }

    modifyFilterListStyles(filterWrapperRef.current);
    setSearchInputFocus(filterWrapperRef.current);
  }, [visible]);

  const checkable = typeof onCheck === 'function';
  const selectable = typeof onSelect === 'function';

  const searchIcon = searching ? <Spin size="small" /> : <FaIcon modifier="fal" icon="search" />;

  return (
    <div className="top-menu-field-group-filter" ref={filterWrapperRef}>
      <div className="top-menu-field-group-filter__search-container">
        <Input
          onChange={onChangeSearchValue}
          placeholder={I18n.t('search')}
          value={searchValue}
          suffix={searchIcon}
        />
      </div>

      <Tree
        checkable={checkable}
        checkedKeys={checkedKeys}
        checkStrictly
        defaultExpandAll={expandAll}
        defaultExpandedKeys={expandKeys}
        style={{ minHeight: drawerHeight - STATIC_OFFSET }}
        height={drawerHeight - STATIC_OFFSET}
        onCheck={onCheck}
        onSelect={onSelect}
        rootClassName="top-menu-field-group-filter__list"
        selectable={selectable}
        selectedKeys={selectedKeys}
        // @ts-ignore
        titleRender={Item}
        treeData={filteredItems}
      />
    </div>
  );
};

FieldGroupsSettings.propTypes = {
  checkedKeys: shape({
    checked: arrayOf(string),
    halfChecked: arrayOf(string),
  }),
  items: arrayOf(shape({
    active: bool,
    ancestorIds: arrayOf(number),
    // eslint-disable-next-line react/forbid-prop-types
    children: arrayOf(object),
    depth: number,
    hasSubfolders: bool,
    id: oneOfType([number, string]),
    key: string,
    title: string,
    type: string,
  })),
  onCheck: func,
  onSelect: func,
  plainItemsIndex: instanceOf(Map),
  selectedKeys: arrayOf(string),
  visible: bool.isRequired,
  expandAll: bool,
  expandKeys: arrayOf(string),
  drawerHeight: number.isRequired,
};

export default FieldGroupsSettings;
